import { isWeb } from "@helpers/platform";
import useMixedItemDist from "@hooks/useMixedItemDist";
import { Box, DialogWrapper, Loader } from "@memorang/ui";
import { usePathname, useRouter } from "expo-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ScrollView } from "react-native";
import { Dialog as PaperDialog, Text } from "react-native-paper";
import { getProethicaOrderedEntries } from "../helpers/proethica-module-list";
import { useProethicaModuleList } from "../hooks/useProethicaModuleList";
import { ModuleListItem, type ModuleListItemProps } from "./ModuleListItem";
import { PurchaseDialog } from "./PurchaseDialog";

type Module = ModuleListItemProps & {
	id: string;
	moduleTitle?: string;
	moduleNumber?: number;
	durationLabel?: string;
};

type Props = {
	visible: boolean;
	handleClose: () => void;
};

export const QuickStudyDialog = ({ visible, handleClose }: Props) => {
	const { t } = useTranslation();
	const router = useRouter();
	const pathname = usePathname();
	const openPathnameRef = useRef(pathname);
	useEffect(() => {
		if (pathname !== openPathnameRef.current) {
			handleClose();
		}
	}, [pathname, handleClose]);
	const { data: mixedItemDist, isLoading } = useMixedItemDist();
	const {
		getModulesGroupedByStatus,
		startModule,
		startingModuleId,
		resolveModuleItems,
	} = useProethicaModuleList();
	const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);

	const items = getProethicaOrderedEntries(mixedItemDist?.exams);

	const sections = useMemo(
		() => getModulesGroupedByStatus(resolveModuleItems(items), t),
		[items, t, getModulesGroupedByStatus, resolveModuleItems],
	);

	const handleModulePress = async (module: Module) => {
		if (module.hasAccess === false) {
			if (isWeb) {
				setShowPurchaseDialog(true);
				return;
			}
			handleClose();
			router.push({
				pathname: "/purchase",
				params: { productType: "STUDY_PACK" },
			});
			return;
		}

		if (module.disabled) {
			return;
		}

		await startModule(module.id);
		handleClose();
	};

	return (
		<>
			<DialogWrapper
				visible={visible}
				handleClose={handleClose}
				width={520}
				showCloseButton
			>
				<PaperDialog.Title>{t("dashboard.quickStudy.title")}</PaperDialog.Title>
				<PaperDialog.ScrollArea style={{ paddingHorizontal: 0 }}>
					<ScrollView contentContainerStyle={{ paddingBottom: 16 }}>
						{isLoading || !mixedItemDist ? (
							<Loader />
						) : (
							<Box style={{ gap: 8 }}>
								<Text
									variant="bodyMedium"
									style={{ paddingHorizontal: 24, paddingBottom: 8 }}
								>
									{t("dashboard.quickStudy.subtitle")}
								</Text>
								{sections.map((section) => (
									<Box key={section.status}>
										<Text
											variant="titleSmall"
											style={{
												paddingHorizontal: 24,
												paddingVertical: 8,
												opacity: 0.8,
											}}
										>
											{section.title}
										</Text>
										{section.modules.map((module) => (
											<ModuleListItem
												key={module.id}
												{...module}
												loading={startingModuleId === module.id}
												onPress={() => handleModulePress(module)}
											/>
										))}
									</Box>
								))}
							</Box>
						)}
					</ScrollView>
				</PaperDialog.ScrollArea>
			</DialogWrapper>
			{showPurchaseDialog && (
				<PurchaseDialog
					visible={showPurchaseDialog}
					handleClose={() => setShowPurchaseDialog(false)}
					productType="STUDY_PACK"
				/>
			)}
		</>
	);
};
